import { useAuth } from "@/contexts/AuthContext";
import { useCaseService } from "@/hooks/useCaseService";
import type {
  CaseListItem,
  CaseUser,
  UpdateCasePhysicianResponse,
} from "@/services/caseServiceV2.types";
import { getErrorMessage } from "@/utils/errorUtils";
import useSWR, { mutate } from "swr";

const CASES_KEY = "/cases";

const isCasesCacheKey = (key: unknown): boolean => Array.isArray(key) && key[0] === CASES_KEY;

export const useCases = () => {
  const { firmContext, isMyCases } = useAuth();
  const caseServiceV2 = useCaseService();

  const cacheKey = isMyCases
    ? [CASES_KEY, "assigned"]
    : firmContext?.firmId
      ? [CASES_KEY, firmContext.firmId]
      : null;

  const {
    data,
    error,
    isLoading,
    mutate: revalidate,
  } = useSWR<CaseListItem[]>(
    cacheKey,
    () => {
      if (isMyCases) return caseServiceV2.getAllAssignedCases();
      if (!firmContext?.firmId) throw new Error("Firm context is not available");
      return caseServiceV2.getAllCases({ firm_id: firmContext.firmId });
    },
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      // Status, file/page counts and unresolved errors are advanced by the worker
      // pipeline, so the list has to poll rather than wait on a user action. SWR's
      // refreshWhenHidden defaults to false, so this only runs while the tab is visible.
      refreshInterval: 60000,
      dedupingInterval: 5000,
    }
  );

  return {
    cases: data || [],
    isLoading,
    isError: Boolean(error),
    error,
    errorMessage: getErrorMessage(error),
    refetch: () => revalidate(),
  };
};

// Single-argument mutate: passing `undefined` as a second arg would write it into the
// cache before refetching, blanking the list mid-revalidation.
export const invalidateCasesCache = (): void => {
  mutate(isCasesCacheKey);
};

const patchCasesCache = (updater: (cases: CaseListItem[]) => CaseListItem[]): void => {
  mutate(
    isCasesCacheKey,
    (current: CaseListItem[] | undefined) => (current ? updater(current) : current),
    { revalidate: false }
  );
};

export const removeCaseFromCache = (caseId: string): void => {
  patchCasesCache((cases) => cases.filter((c) => c.case_id !== caseId));
};

export const updateCaseInCache = (caseId: string, patch: Partial<CaseListItem>): void => {
  patchCasesCache((cases) => cases.map((c) => (c.case_id === caseId ? { ...c, ...patch } : c)));
};

export const addCaseAnalystToCache = (caseId: string, analyst: CaseUser): void => {
  patchCasesCache((cases) =>
    cases.map((c) =>
      c.case_id === caseId ? { ...c, analysts: [...(c.analysts ?? []), analyst] } : c
    )
  );
};

export const removeCaseAnalystFromCache = (caseId: string, userId: string): void => {
  patchCasesCache((cases) =>
    cases.map((c) =>
      c.case_id === caseId
        ? { ...c, analysts: (c.analysts ?? []).filter((a) => a.user_id !== userId) }
        : c
    )
  );
};

export const addCaseQcReviewerToCache = (caseId: string, qcReviewer: CaseUser): void => {
  patchCasesCache((cases) =>
    cases.map((c) =>
      c.case_id === caseId ? { ...c, qc_reviewers: [...(c.qc_reviewers ?? []), qcReviewer] } : c
    )
  );
};

export const removeCaseQcReviewerFromCache = (caseId: string, userId: string): void => {
  patchCasesCache((cases) =>
    cases.map((c) =>
      c.case_id === caseId
        ? { ...c, qc_reviewers: (c.qc_reviewers ?? []).filter((r) => r.user_id !== userId) }
        : c
    )
  );
};

export const updateCasePhysicianInCache = (
  caseId: string,
  physician: UpdateCasePhysicianResponse
): void => {
  updateCaseInCache(caseId, {
    physician_id: physician.physician_id,
    physician: physician.physician_name,
  });
};
